// CoverMyClass App State Management with localStorage persistence

import { getOnboardingState, OnboardingState, StaffMember, type PolicyDocument } from './onboarding-store';
import { classifyPolicyFileName, policyDisplayName, type PolicySlotKind } from './policy-naming';

// Valid invite codes storage
const INVITE_CODES_KEY = 'cmc_invite_codes';
const APP_STATE_KEY = 'cmc_app_state';
const TUTORIAL_STATE_KEY = 'cmc_tutorial_state';
const COVER_TUTORIAL_STATE_KEY = 'cmc_cover_tutorial_state';
/**
 * The school/admin tour's own key.
 *
 * It used to read and write `cmc_tutorial_state` — the TEACHER tour's key —
 * which is rule 3.5 ("independent tours must not share a storage key") and the
 * reason tours came back after a login. Two ways it bit:
 *
 *   - An admin previewing the teacher portal RESETS that key to
 *     {completed:false} so the teacher tour will run. That also re-armed the
 *     admin's own tour, so it replayed on their next load.
 *   - On a shared school browser, an admin finishing their tour marked the
 *     teacher tour complete too, so the next teacher to log in never got one.
 */
const SCHOOL_TUTORIAL_STATE_KEY = 'cmc_school_tutorial_state';
const COVER_ASSIGNMENTS_KEY = 'cmc_cover_assignments';
const EMERGENCY_ALERTS_KEY = 'cmc_emergency_alerts';

export interface InviteCode {
  code: string;
  email: string;
  teacherName: string;
  department: string;
  schoolId: string;
  createdAt: number;
  usedAt?: number;
  isValid: boolean;
}

export interface Period {
  number: string;
  startTime: string;
  endTime: string;
}

export interface School {
  name: string;
  logo: string;
  logoImage: string | null;
  primary: string;
  urn?: string;
  dfeNumber?: string;
  address?: string;
  postcode?: string;
  staff: StaffMember[];
  subjects: string[];
  rooms: string[];
  yearGroups: string[];
  periods: Period[];
  /** Rotation length. 1 = single-week (default). 2+ = rotating multi-week timetable. */
  weekPattern?: number;
  /** Per-week period lists. weekPeriods[0] mirrors `periods` (Week 1).
   *  weekPeriods[i] holds the periods for week (i+1) of the rotation. */
  weekPeriods?: Period[][];
  /** ISO date (Monday) anchoring Week 1 of the rotation. */
  weekAnchorDate?: string;
  guestAccount?: {
    username: string;
    password: string;
    notes: string;
  };
  contacts?: {
    itSupport?: { name: string; email: string; phone: string };
    coverCoordinator?: { name: string; email: string; phone: string };
    headTeacher?: { name: string; email: string; phone: string };
    safeguarding?: { name: string; email: string; phone: string };
    adminEmail?: string;
    secondaryContacts?: { name: string; email: string; role: string; position?: string; phone?: string }[];
  };
  policies?: {
    behaviourPolicy?: { extractedPoints?: string[]; url?: string; fileName?: string; keyPoints?: string; blobPath?: string };
    safeguardingPolicy?: { extractedPoints?: string[]; url?: string; fileName?: string; keyPoints?: string; blobPath?: string };
    coverPolicy?: { extractedPoints?: string[]; url?: string; fileName?: string; keyPoints?: string; blobPath?: string };
    additionalPolicies?: { type: string; name?: string; fileName?: string; keyPoints?: string; url?: string; blobPath?: string }[];
  };
  coverTeacherRules?: string[];
  departments?: string[];
  /** Annual retention review for archived cover observations (Stage 2 lifecycle).
   *  Mirrors SchoolConfig.observationRetention in api.ts. Defaults applied via
   *  resolveRetentionPolicy(). */
  observationRetention?: {
    enabled?: boolean;
    reviewMonth?: number;
    reviewDay?: number;
    requireApproval?: boolean;
    graceDays?: number;
  };
}

export interface LessonStep {
  id: string;
  minutes: number;
  title: string;
  detail: string;
  // Phase 2e — block-based lesson builder. The brick kinds replace the
  // single up-front task-type gate; one lesson can now mix and match
  // any number of kinds in any order. `kind` is optional so legacy
  // steps (and the demo seeds) keep rendering as plain 'custom' blocks.
  kind?: LessonBlockKind;
  /** Some kinds carry extra structured fields on top of title + detail.
   *  All optional so the schema stays simple and back-compat-safe. */
  videoUrl?: string;
  /** Pointer(s) into Lesson.attachments — kept for legacy 'worksheet'
   *  blocks that referenced lesson-level attachments. New attachment /
   *  photo / voice-note blocks store their files inline below. */
  attachmentIds?: string[];
  // Phase 2e.2 — inline media slots. Any block kind can carry up to
  // one of each media type, gated by its definition's availableMedia.
  // Files live INSIDE the block they belong to, so the lesson reads
  // as a single composable timeline.
  /** Single file attached to this block (PDF, docx, image, anything).
   *  `blobPath` is the canonical reference — the file lives in Azure
   *  Blob Storage at <schoolId>/<lessonId>/<stepId>-<uid>-<name>. The
   *  browser fetches it via a short-lived read SAS issued at view time.
   *  `dataUrl` is kept ONLY for back-compat with older lessons that
   *  inlined the file as base64 in Cosmos. New uploads never set it. */
  attachment?: {
    name: string;
    type: string;
    size: number;
    /** Blob Storage path (preferred — files of any size). */
    blobPath?: string;
    /** Legacy inline base64 (small files in older lessons). */
    dataUrl?: string;
  };
  /** Single inline photo (board snap, worked example, stimulus image).
   *  Same blobPath / dataUrl story as attachment above. */
  photo?: {
    name: string;
    /** Blob Storage path (preferred). */
    blobPath?: string;
    /** Legacy inline base64. New uploads never set this. */
    dataUrl?: string;
  };
  /** Single resource link — URL or UNC path the cover teacher opens. */
  link?: {
    url: string;
    label?: string;
  };
  // Phase 2e.15 — MULTIPLE media per block. The single attachment / photo
  // / link above are kept so lessons saved before this still deserialise;
  // new uploads append to these arrays. Read the combined list with
  // stepAttachments / stepPhotos / stepLinks (they merge legacy + array).
  attachments?: Array<{ name: string; type: string; size: number; blobPath?: string; dataUrl?: string }>;
  photos?: Array<{ name: string; blobPath?: string; dataUrl?: string }>;
  links?: Array<{ url: string; label?: string }>;
  /** @deprecated Voice notes were retired — the field is left on the
   *  type so any legacy lessons in Cosmos that already carry one
   *  still deserialise without errors. Nothing reads or writes it. */
  voiceNote?: {
    dataUrl: string;
    durationSec: number;
    transcript?: string;
  };
  /** The "flip side" of a block — where the cover teacher finds out
   *  whether the class got the work right. Same shape as the block's
   *  own text/attachment/photo/link, just never shown to pupils.
   *  Optional and independent per block; the readiness checklist is
   *  satisfied once at least one block in the lesson has content here. */
  answerKey?: {
    detail: string;
    attachment?: {
      name: string;
      type: string;
      size: number;
      blobPath?: string;
      dataUrl?: string;
    };
    photo?: {
      name: string;
      blobPath?: string;
      dataUrl?: string;
    };
    link?: {
      url: string;
      label?: string;
    };
    // MULTIPLE media on the answer key too (back-compat, same as the block).
    attachments?: Array<{ name: string; type: string; size: number; blobPath?: string; dataUrl?: string }>;
    photos?: Array<{ name: string; blobPath?: string; dataUrl?: string }>;
    links?: Array<{ url: string; label?: string }>;
  };
  /** What a pupil does if they finish THIS block early. Attached per-block
   *  (any activity can produce early finishers) rather than as a standalone
   *  block. The readiness "early finishers" item is satisfied once any block
   *  carries one. Shown to the cover teacher under the block's task. */
  earlyFinishers?: string;
}

// ── Multiple-media helpers ──────────────────────────────────────────
// Merge a block's (or answer key's) legacy single media field with its
// new array field into one list. The legacy single item comes last so
// freshly-added array items read first. Accepts a LessonStep or an
// answerKey (structurally identical media fields).
type MediaHost = {
  attachment?: LessonStep['attachment'];
  attachments?: LessonStep['attachments'];
  photo?: LessonStep['photo'];
  photos?: LessonStep['photos'];
  link?: LessonStep['link'];
  links?: LessonStep['links'];
};
export function stepAttachments(m?: MediaHost): NonNullable<LessonStep['attachments']> {
  return [...(m?.attachments ?? []), ...(m?.attachment ? [m.attachment] : [])];
}
export function stepPhotos(m?: MediaHost): NonNullable<LessonStep['photos']> {
  return [...(m?.photos ?? []), ...(m?.photo ? [m.photo] : [])];
}
export function stepLinks(m?: MediaHost): NonNullable<LessonStep['links']> {
  return [...(m?.links ?? []), ...(m?.link ? [m.link] : [])];
}

// ── Phase 2e: Lesson block kinds ────────────────────────────────────
//
// Replaces the up-front TaskTypePicker (one shape per lesson) with a
// composable palette of bricks. A lesson now consists of any number
/**
 * The emergency door's "What does your class do with finished work?" answer.
 * A stable synthetic step id, not a uid(), so a reopened draft re-populates the
 * right field — and so the readiness checklist can ask whether that exact
 * question was answered instead of scanning prose for words like "leave" and
 * "collect", which tick a BLOCKER from a sentence that was never about
 * finished work at all ("leave them to work quietly").
 *
 * Lives here rather than in EmergencyCoverForm so the checklist can read it
 * without pulling a component tree into every chunk that renders the checklist.
 */
export const EMERGENCY_HANDBACK_STEP_ID = 'emergency-handback';

// of blocks in any order — typical mix might be a Retrieval starter +
// a Worksheet + an Exit ticket. Each brick is a LessonStep with a
// `kind` set; legacy steps without a kind render as 'custom'.

export type LessonBlockKind =
  | 'retrieval'
  | 'reading'
  | 'practice'
  | 'video'
  | 'vocabulary'
  | 'worksheet'
  | 'discussion'
  | 'exit_ticket'
  | 'early_finisher'
  | 'cover_note'
  | 'custom';

/** Which inline media affordances a block of this kind can carry.
 *  Drives the row of "+ Attach / + Photo / + Link" buttons shown
 *  below the detail textarea. Voice notes were retired — the AI
 *  ghost-text + polish assist replaced the briefing use case. */
export type LessonBlockMedia = 'attachment' | 'photo' | 'link';

/** Lesson-stage grouping for the side palette. Communicates the
 *  expected structure of a lesson (beginning / middle / end) so a
 *  teacher seeing the palette understands what kind of block belongs
 *  where without having to read instructions. */
export type LessonBlockStage = 'start' | 'middle' | 'end' | 'other';

export interface LessonBlockDefinition {
  id: LessonBlockKind;
  /** Display label on the palette card and the block header. */
  label: string;
  /** One-line summary on the palette card. Teacher-tone. */
  summary: string;
  /** Lucide icon name — palette + block header both use this. */
  iconKey:
    | 'help-circle'
    | 'book-open'
    | 'sigma'
    | 'play-circle'
    | 'spell-check'
    | 'paperclip'
    | 'message-circle'
    | 'check-square'
    | 'zap'
    | 'sticky-note'
    | 'square-plus';
  /** Sensible default — the teacher can override but most won't bother. */
  defaultMinutes: number;
  /** Smart placeholder for the title input — supports {currentUnit} which
   *  the renderer substitutes with the class's currentUnit pillar. */
  titlePlaceholder: string;
  /** Placeholder for the detail body. Tells the teacher what to put in. */
  detailPlaceholder: string;
  /** Whether this block needs the videoUrl field. */
  hasVideoUrl?: boolean;
  /** Whether this block is non-pupil-facing (note to the cover teacher). */
  isCoverNote?: boolean;
  /** Which inline media affordances this block kind exposes. */
  availableMedia: LessonBlockMedia[];
  /** Lesson-stage grouping for the side palette. */
  stage: LessonBlockStage;
  /** Hidden from the palette (not offered as a new block) but its definition
   *  is kept so legacy lessons that already use the kind still render. */
  hiddenFromPalette?: boolean;
}

// Convenience — most blocks accept all three media affordances.
const ALL_MEDIA: LessonBlockMedia[] = ['attachment', 'photo', 'link'];

export const BLOCK_DEFINITIONS: LessonBlockDefinition[] = [
  {
    id: 'retrieval',
    label: 'Retrieval starter',
    summary: 'Quick questions to recall what they\'ve already been taught.',
    iconKey: 'help-circle',
    defaultMinutes: 5,
    titlePlaceholder: 'Recap: {currentUnit}',
    detailPlaceholder: 'List 4–6 quick questions students should know by now. Flip the card over to add the answer key.',
    availableMedia: ALL_MEDIA,
    stage: 'start',
  },
  {
    id: 'reading',
    label: 'Reading + questions',
    summary: 'Read a passage, then answer set questions in writing.',
    iconKey: 'book-open',
    defaultMinutes: 15,
    titlePlaceholder: 'Reading: {currentUnit}',
    detailPlaceholder: 'Paste the passage (or attach it) and list the questions students answer. Flip the card over to add the answer key.',
    availableMedia: ALL_MEDIA,
    stage: 'middle',
  },
  {
    id: 'practice',
    label: 'Practice a taught method',
    summary: 'Mechanical practice of a method the class has already met.',
    iconKey: 'sigma',
    defaultMinutes: 20,
    titlePlaceholder: 'Practice: {currentUnit}',
    detailPlaceholder: 'Briefly remind the method (one or two lines) and list the problems. Flip the card over to add the answer key.',
    availableMedia: ALL_MEDIA,
    stage: 'middle',
  },
  {
    id: 'video',
    label: 'Video + response',
    summary: 'Watch a short clip, then a structured written task.',
    iconKey: 'play-circle',
    defaultMinutes: 15,
    titlePlaceholder: 'Watch: {currentUnit}',
    detailPlaceholder: 'Tell the cover teacher the video title, the writing prompts, and add a printed backup in case the projector fails.',
    hasVideoUrl: true,
    availableMedia: ALL_MEDIA,
    stage: 'middle',
  },
  {
    id: 'vocabulary',
    label: 'Vocabulary drill',
    summary: 'Words and what students should produce for each.',
    iconKey: 'spell-check',
    defaultMinutes: 15,
    titlePlaceholder: 'Vocab: {currentUnit}',
    detailPlaceholder: 'List the words and what students should produce: definitions, example sentences, translations…',
    availableMedia: ALL_MEDIA,
    stage: 'middle',
  },
  {
    id: 'worksheet',
    label: 'Worksheet (attached)',
    summary: 'A printed or digital worksheet you\'ve already got.',
    iconKey: 'paperclip',
    defaultMinutes: 25,
    titlePlaceholder: 'Worksheet: {currentUnit}',
    detailPlaceholder: 'One or two lines: what the worksheet is, what students should produce, where it goes when finished.',
    availableMedia: ALL_MEDIA,
    stage: 'middle',
  },
  {
    id: 'discussion',
    label: 'Discussion',
    summary: 'A paired or whole-class talking task.',
    iconKey: 'message-circle',
    defaultMinutes: 10,
    titlePlaceholder: 'Discuss: {currentUnit}',
    detailPlaceholder: 'The discussion prompt, the grouping (pairs / fours / whole class), and what they should produce by the end.',
    availableMedia: ['photo', 'link'],
    stage: 'middle',
  },
  {
    id: 'exit_ticket',
    label: 'Exit ticket',
    summary: 'A quick check at the end of the lesson.',
    iconKey: 'check-square',
    defaultMinutes: 5,
    titlePlaceholder: 'Exit ticket',
    detailPlaceholder: '1–3 quick questions for students to answer on paper before they leave.',
    availableMedia: ['attachment', 'photo'],
    stage: 'end',
  },
  {
    // Retired as a standalone block — early finishers is now a per-block note
    // (LessonStep.earlyFinishers) since ANY activity can produce them. Kept here
    // (hidden from the palette) so lessons saved with the old block still render.
    id: 'early_finisher',
    label: 'Early finishers',
    summary: 'What a pupil does if they finish the main task with time to spare.',
    iconKey: 'zap',
    defaultMinutes: 10,
    titlePlaceholder: 'If they finish early',
    detailPlaceholder: 'A quick extension, a stretch question, wider reading. Anything ready to go so nobody sits idle.',
    availableMedia: ALL_MEDIA,
    stage: 'other',
    hiddenFromPalette: true,
  },
  {
    id: 'cover_note',
    label: 'Note to the cover teacher',
    summary: 'A private heads-up, context, not an activity.',
    iconKey: 'sticky-note',
    defaultMinutes: 0,
    titlePlaceholder: 'For the cover teacher',
    detailPlaceholder: 'Anything the cover teacher needs to know that isn\'t pupil-facing, seating, who to refer to SLT, where the spare worksheets are.',
    isCoverNote: true,
    availableMedia: [],
    stage: 'other',
  },
  {
    id: 'custom',
    label: 'Custom',
    summary: 'Anything else, describe it yourself.',
    iconKey: 'square-plus',
    defaultMinutes: 10,
    titlePlaceholder: '',
    detailPlaceholder: 'Describe the activity in plain English. Include anything the cover teacher would need to lead it.',
    availableMedia: ALL_MEDIA,
    stage: 'other',
  },
];

/** Lookup by kind id. Returns the 'custom' definition as a fallback so
 *  unknown / legacy kinds always render safely. */
export function getBlockDefinition(kind: LessonBlockKind | undefined): LessonBlockDefinition {
  if (!kind) return BLOCK_DEFINITIONS.find(b => b.id === 'custom')!;
  return BLOCK_DEFINITIONS.find(b => b.id === kind)
    ?? BLOCK_DEFINITIONS.find(b => b.id === 'custom')!;
}

/** The six task shapes a cover lesson can take. Replaces the 15 procedural
 *  templates (which all shared the same skeleton) with a smaller set of
 *  genuinely different lesson shapes. Each shape drives task-specific
 *  hints in the lesson builder; the underlying lesson schema stays the
 *  same so cover teachers see consistent rendering downstream. */
export type TaskType =
  | 'retrieval_quiz'
  | 'reading_questions'
  | 'practice_method'
  | 'video_response'
  | 'vocabulary_drill'
  | 'upload_and_go';

export interface TaskTypeDefinition {
  id: TaskType;
  /** Short label shown on the picker card and as a chip in the builder. */
  label: string;
  /** One-line summary — sits under the label on the picker card. */
  summary: string;
  /** Concrete example a teacher recognises ("Y9 Maths revising linear equations"). */
  example: string;
  /** Lucide icon name — kept as a string so the picker can import it lazily. */
  iconKey:
    | 'help-circle'
    | 'book-open'
    | 'sigma'
    | 'play-circle'
    | 'spell-check'
    | 'paperclip';
  /** Placeholder for the lesson builder's main task field when this type
   *  is selected. Replaces the generic placeholder copy. */
  mainTaskPlaceholder: string;
  /** A line of guidance shown at the top of the lesson builder so the
   *  teacher remembers what shape of task this is meant to be. */
  builderHint: string;
}

export const TASK_TYPE_DEFINITIONS: TaskTypeDefinition[] = [
  {
    id: 'retrieval_quiz',
    label: 'Retrieval quiz',
    summary: 'Practice recalling content the class has already been taught.',
    example: 'Year 9 Maths, quick-fire questions on the algebra unit they\'ve just finished.',
    iconKey: 'help-circle',
    mainTaskPlaceholder: 'List the questions students should answer, and the answer key for the cover teacher.',
    builderHint: 'Cover-ideal: no new content to teach, students work independently, self-marking via answer key.',
  },
  {
    id: 'reading_questions',
    label: 'Structured reading + questions',
    summary: 'Students read a passage or text and answer set questions in writing.',
    example: 'Year 10 English. Read a chapter extract, answer 5 comprehension questions.',
    iconKey: 'book-open',
    mainTaskPlaceholder: 'Paste the text (or attach it) and list the questions. Include an answer key or success criteria.',
    builderHint: 'Cover-ideal: self-contained text + questions. Best when the text doesn\'t need teacher explanation.',
  },
  {
    id: 'practice_method',
    label: 'Practice a taught method',
    summary: 'Work through problems using a method already covered in class.',
    example: 'Year 8 Maths, apply long division to a worksheet of 12 problems.',
    iconKey: 'sigma',
    mainTaskPlaceholder: 'Describe the method (briefly), list the problems, and include the answer key.',
    builderHint: 'Cover-ideal: low ambiguity, mechanical practice, easy to spot who\'s stuck without subject knowledge.',
  },
  {
    id: 'video_response',
    label: 'Video + written response',
    summary: 'Watch a short clip, then complete a structured writing task on it.',
    example: 'Year 7 History, watch a 12-min documentary clip, answer 5 prompts in their books.',
    iconKey: 'play-circle',
    mainTaskPlaceholder: 'Link or describe the video, list the writing prompts, set the time budget. Add a printed backup in case tech fails.',
    builderHint: 'Cover-ideal: light cover-teacher lift, but always plan a backup for if the projector/wifi fails.',
  },
  {
    id: 'vocabulary_drill',
    label: 'Vocabulary drill',
    summary: 'Definitions, example sentences, or translations to practise.',
    example: 'Year 10 French, 15 new vocab items, write the definition + a sentence using each.',
    iconKey: 'spell-check',
    mainTaskPlaceholder: 'List the vocabulary items + what students should produce for each (definition, example, translation).',
    builderHint: 'Cover-ideal: simple, structured, no subject-specialist explanation needed.',
  },
  {
    id: 'upload_and_go',
    label: 'Upload-and-go',
    summary: 'Attach an existing worksheet, booklet, or activity. Add a one-line briefing.',
    example: 'Any subject, past-paper booklet you\'ve already got printed; cover teacher hands it out and supervises.',
    iconKey: 'paperclip',
    mainTaskPlaceholder: 'Brief the cover teacher: what the attachment is, what students should produce, where it goes when done.',
    builderHint: 'Cover-ideal when you already have ready-made materials. Keep the briefing short. The attachment does the work.',
  },
];

export interface LessonResource {
  id: string;
  name: string;
  location: string;
  type?: string;
}

export interface Lesson {
  id: string;
  status: 'draft' | 'published' | 'awaiting_approval' | 'approved' | 'rejected';
  createdAt: number;
  dateISO: string;
  classroom: string;
  subject: string;
  yearGroup: string;
  unit?: string;
  periodNumber: string;
  periodTime: string;
  coveringFor: string;
  /** Optional link to a stable Class entity. Added in Phase 1 of the
   *  redesign — legacy lessons leave this undefined and continue to use
   *  the free-text coveringFor + subject + yearGroup combination. */
  classId?: string;
  lessonAim: string;
  objectives: string[];
  steps: LessonStep[];
  resources: LessonResource[];
  resourcePaths: string[];
  photos: LessonPhoto[];
  notes: string;
  notifications?: { notifyHoD?: boolean; notifyCoverCoordinator?: boolean };
  template?: string;
  coverageType?: 'single_period' | 'multiple_periods' | 'full_day';
  endDateISO?: string;
  teacherId?: string;
  // Phase 2 of the redesign — the create-flow door this lesson came from.
  // Drives downstream copy + defaults; absent on legacy lessons.
  coverDoor?: 'emergency' | 'planned';
  // Phase 2 — the task type the teacher picked (Phase 2b will populate
  // this from the new task-type picker; Phase 2a leaves it undefined).
  taskType?: TaskType;
  // Phase 2 (planned door) — one-line "what students should come back
  // having done" that replaces the lessonAim paragraph for planned cover.
  // Optional; legacy lessons + emergency cover may leave it undefined.
  continuityGoal?: string;
  // Phase 2d.2 (emergency door) — the "anything different about today?"
  // free-text the teacher provides on top of what the class continuity
  // pillars already supply. Examples: a pupil absence, a temporary room
  // change, a half-finished worksheet from last lesson. Sits separate
  // from `notes` (which is generic cover-teacher briefing) and
  // `lessonAim` (the curriculum task itself).
  todaySpecifics?: string;
  /** Phase 2c — Multiple-period bookings group several independent lessons
   *  under one absence. Lessons sharing this id are created together,
   *  rendered as one row in the drafts/submitted list, submitted /
   *  approved as a group by default, and can be revoked together. A
   *  Lesson without coverRequestId is a standalone single-period booking. */
  coverRequestId?: string;
  /** Optional 0-indexed week override for multi-week timetables. When set, the
   *  period dropdown uses this week's periods instead of the one auto-derived
   *  from the lesson date. Leave unset to use the date-based default. */
  weekIndex?: number;
  // Approval workflow fields
  publishedAt?: number;
  approvedAt?: number;
  approvedBy?: string;
  approvedByEmail?: string;
  rejectedAt?: number;
  rejectedByEmail?: string;
  rejectedBy?: string;
  rejectionReason?: string;
  // File attachments uploaded by teacher; dataUrl present for files ≤1 MB
  /** `blobPath` for direct-to-blob uploads, `dataUrl` for the legacy inline
   *  path. Block-level attachments (LessonStep.attachments) have carried both
   *  for a while; the lesson-level type only knew about dataUrl, so readers
   *  had nothing to resolve and fell back to an unopenable placeholder. */
  attachments?: { id: string; name: string; type: string; size: number; description?: string; dataUrl?: string; blobPath?: string }[];
  // Cover teacher feedback attached to this lesson after completion
  feedback?: Feedback;
  feedbackAt?: number;
  teacherEmail?: string;
}

export interface LessonPhoto {
  id: string;
  name: string;
  dataUrl: string;
}

// ============================================================================
// COVER-ASSIGNABLE TEACHERS
//
// The cover assignment dropdown originally listed only school.staff, which
// quietly excluded the primary school admin and any secondary contacts —
// the people most likely to step in when there's no other cover available.
// This helper unions all three sources behind a single shape so the modals
// (CoverAssignmentModal / BulkCoverAssignmentModal) just iterate one list
// without caring where each entry came from.
// ============================================================================

export type AssignableSource = 'staff' | 'primary_admin' | 'secondary_contact';

export interface AssignableTeacher {
  /** Synthetic stable id — 'staff-<id>', 'admin-primary', or
   *  'admin-secondary-<email>'. Used only as the dropdown's React key
   *  and selection state; the canonical identity for sending the
   *  notification is always email. */
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  /** What to show on the right side of the dropdown option — real
   *  department for staff, 'School admin' for admins, the contact's
   *  role for secondary contacts. */
  department: string;
  source: AssignableSource;
}

/** Build the full list of teachers an admin can pick from for cover.
 *  Dedupes by email so a staff member who's ALSO listed as a secondary
 *  contact appears once (staff entry wins — that's the row admins
 *  already maintain). Terminated staff excluded; admins with no email
 *  on file excluded. */
export function getAssignableTeachers(school: School): AssignableTeacher[] {
  const out: AssignableTeacher[] = [];
  const seen = new Set<string>();

  for (const s of school.staff ?? []) {
    if (s.status === 'terminated') continue;
    const email = (s.email ?? '').toLowerCase();
    if (email && seen.has(email)) continue;
    if (email) seen.add(email);
    out.push({
      id: `staff-${s.id}`,
      firstName: s.firstName,
      lastName: s.lastName,
      email: s.email,
      department: s.department || '',
      source: 'staff',
    });
  }

  const adminEmail = school.contacts?.adminEmail?.trim();
  if (adminEmail && !seen.has(adminEmail.toLowerCase())) {
    seen.add(adminEmail.toLowerCase());
    const fullName = (school.contacts?.headTeacher?.name ?? '').trim();
    const [firstName = 'School', ...rest] = fullName.split(/\s+/);
    out.push({
      id: 'admin-primary',
      firstName,
      lastName: rest.join(' ') || 'Admin',
      email: adminEmail,
      department: 'School admin',
      source: 'primary_admin',
    });
  }

  for (const c of school.contacts?.secondaryContacts ?? []) {
    const email = (c.email ?? '').toLowerCase();
    if (!email || seen.has(email)) continue;
    seen.add(email);
    const fullName = (c.name ?? '').trim();
    const [firstName = 'Secondary', ...rest] = fullName.split(/\s+/);
    out.push({
      id: `admin-secondary-${email}`,
      firstName,
      lastName: rest.join(' ') || 'Contact',
      email: c.email,
      department: c.role || 'School admin',
      source: 'secondary_contact',
    });
  }

  return out;
}

export interface FeedbackRating {
  category: string;
  score: number;
  comment?: string;
}

export interface Feedback {
  id: string;
  lessonId: string;
  subject: string;
  yearGroup: string;
  ratings: FeedbackRating[];
  createdAt: number;
  /** Phase 2f — open-ended summary the cover teacher writes at the end
   *  of the wizard. Goes to admin only; never feeds the class profile. */
  adminSummary?: string;
  // Legacy fields for backwards compat
  completion?: string;
  rating?: number;
  positives?: string;
  challenges?: string;
  behaviour?: string;
  suggestions?: string;
}

// ============================================================================
// PILLAR SUGGESTION (Phase 2f — cover feedback feeds the class profile)
//
// Cover teachers see one lesson on a class they don't own. Auto-mutating the
// class pillars from their observations would let a single off-day drift the
// profile. Instead, the wizard emits PillarSuggestion docs that the regular
// teacher reviews and accepts or discards from a banner on the class profile.
// Same gating pattern as the lesson approval queue — the cover teacher
// proposes; the authoritative party (the regular teacher) commits.
// ============================================================================

export type PillarSuggestionField =
  // Phase 1 targets (the rebuilt feedback loop writes these)
  | 'pacing'                    // update (enum)
  | 'knownMisconceptions'       // append (PillarEntry[])
  | 'priorKnowledgeCanAlready'  // append → priorKnowledge.canAlready
  | 'whatWorksWithThem'         // append (PillarEntry[])
  | 'behaviourNorm'             // flag only — never written from feedback
  // Legacy targets — kept so any pre-Phase-1 stored suggestion still resolves.
  // The rebuilt wizard no longer emits these; currentUnit feedback is deleted
  // outright (teacher-owned).
  | 'entryRoutine'
  | 'currentUnit'
  | 'equipment'
  | 'otherDistinctives';

export interface PillarSuggestion {
  id: string;
  /** Cosmos doc type discriminator. */
  type: 'pillar_suggestion';
  /** School partition key. */
  schoolId: string;
  /** The class whose profile this targets. */
  classId: string;
  /** The lesson the cover teacher just delivered (provenance). */
  lessonId: string;
  /** Name of the cover teacher shown in the review banner. */
  coverTeacherName: string;
  /** Email — used to attribute the suggestion in the audit trail. */
  coverTeacherEmail?: string;
  /** The pillar field this suggestion would modify. */
  field: PillarSuggestionField;
  /** How applying this suggestion mutates the pillar (Phase 1):
   *   • 'append' — add a PillarEntry to an accumulating array (dedup by exact
   *     normalised text, else new). misconceptions / prior-knowledge / what-works.
   *   • 'update' — replace a scalar value. pacing.
   *   • 'flag'   — surface for the teacher's attention only; NEVER writes text.
   *     behaviour. One bad Friday must not rewrite a taught standard.
   *  Optional so legacy docs deserialise; absent → treated as 'update' on read. */
  kind?: 'append' | 'update' | 'flag';
  /** The new value the cover teacher's answer maps to. For append this is the
   *  entry text; for update the new enum; for flag the (mandatory) comment. */
  suggestedValue: string;
  /** Snapshot of what was in the pillar at submission time, so the
   *  teacher sees the diff even if they edited the profile in between. */
  currentValue?: string;
  /** Pending until the teacher acts on it. Applied/discarded suggestions
   *  stay around for audit but stop rendering in the banner. */
  status: 'pending' | 'applied' | 'discarded';
  createdAt: number;
  /** Set when the teacher resolves the suggestion. */
  resolvedAt?: number;
  /** Teacher ID that applied/discarded — only set on resolution. */
  resolvedBy?: string;
}

// ============================================================================
// CLASS + PUPIL ENTITIES (Phase 1 of the cover-continuity redesign)
//
// A Class is a stable teaching group that lives across the cohort's tenure
// (often multiple academic years for secondary). A Pupil is a stable child
// record carried at school level — they may appear in multiple Classes and
// follow the cohort from Year 7 to Year 13.
//
// The two entities exist so the class profile (cover-teacher brief) has a
// place to thicken across years rather than being re-authored each term.
// Lessons start to link to a Class via Lesson.classId from this phase
// onwards; legacy lessons stay valid with their free-text coveringFor.
// ============================================================================

/** A single recurring slot in the weekly timetable for a class. */
export interface ClassMeetingSlot {
  /** Zero-indexed week of the rotation. 0 = Week 1 / Week A. */
  weekIndex: number;
  /** ISO day-of-week: 1 = Monday … 7 = Sunday. */
  dayOfWeek: number;
  /** Matches School.periods[].number — "1", "2", etc. */
  periodNumber: string;
  /** Optional room override — falls back to Class.defaultRoom. */
  room?: string;
}

/** The shape of a class — drives how it's modelled and rendered. */
export type ClassShape =
  /** Secondary subject class (e.g. "9B French"). */
  | 'secondary'
  /** Primary form class — same children all day with one teacher. */
  | 'primary_form'
  /** Ad-hoc grouping for special days (sports day, trip, themed day). */
  | 'special_day';

// ── Phase 2d.1: Continuity Pillars ──────────────────────────────────
//
// The "pillars" answer the question: "What makes my class different from
// another teacher's class with the same year and subject?" Captured ONCE
// per class so every cover lesson against that class inherits them
// automatically — no need to re-enter exam board, tier, behaviour notes,
// equipment, etc. on every emergency cover request.
//
// Universal pillars apply to most/all subjects. Subject-specific pillars
// (calculator policy, target language ratio, etc.) only surface when the
// subject matches. Custom pillars give schools / teachers an escape hatch
// for anything bespoke.

/** UK exam boards — the publicly-recognised ones we surface in the dropdown. */
export const EXAM_BOARDS = [
  'AQA',
  'Pearson Edexcel',
  'OCR',
  'WJEC',
  'Eduqas',
  'CCEA',
  'Cambridge International',
  'IB',
  'Other',
] as const;
export type ExamBoard = typeof EXAM_BOARDS[number];

export type ClassTier = 'foundation' | 'higher' | 'standard';
export type ClassPacing = 'on_track' | 'ahead' | 'behind';
export type CalculatorPolicy = 'always' | 'never' | 'topic_dependent';
export type TargetLanguageRatio = 'mostly_target' | 'half' | 'mostly_english';

export const EQUIPMENT_OPTIONS = [
  'textbook',
  'exercise_books',
  'chromebooks',
  'calculators',
  'physical_worksheets',
  'whiteboards',
  'projector',
  'other',
] as const;
export type Equipment = typeof EQUIPMENT_OPTIONS[number];

/** Subjects where the calculator-policy pillar makes sense. Case-insensitive
 *  matched against ClassEntity.subject in the UI. */
export const SUBJECTS_USING_CALCULATOR_POLICY = ['mathematics', 'maths', 'science', 'physics', 'chemistry', 'biology'];

/** Subjects where the target-language-ratio pillar makes sense. */
export const SUBJECTS_USING_TARGET_LANGUAGE = ['french', 'german', 'spanish', 'mandarin', 'italian', 'latin', 'mfl', 'modern foreign languages'];

/** Subjects where the lab-safety + kit pillars make sense. */
export const SUBJECTS_USING_LAB_SAFETY = ['science', 'physics', 'chemistry', 'biology'];
export const SUBJECTS_USING_KIT_POLICY = ['pe', 'physical education', 'sport'];

// Note: the structured CustomPillar (label + value pair) was retired in
// favour of a single conversational "Anything else" free-text field
// below. Reading legacy data still works because nothing else in the
// codebase reads customPillars; it's simply ignored on load.

/** One dated, attributed observation inside an accumulating pillar. Cover
 *  feedback appends these (teacher-curated); never hard-deleted — archived. */
export interface PillarEntry {
  id: string;
  text: string;
  createdAt: number;
  source: 'teacher' | 'cover_observation';
  /** Cover teacher's name when source = 'cover_observation'. */
  attributedTo?: string;
  /** The cover lesson that produced this entry (provenance). */
  lessonId?: string;
  /** Dedup + confidence in one: exact-text re-reports increment this (starts 1). */
  confirmCount: number;
  /** Set when auto-archived at cohort boundary (Phase 2); never hard-deleted. */
  archivedAt?: number;
  /** Teacher retrieval request — a teacher can't restore an archived entry
   *  themselves (admin-only), but can flag it so an admin sees the ask.
   *  Cleared when an admin restores it. */
  retrievalRequestedAt?: number;
  retrievalRequestedBy?: string;
  /** Which cohort (group of children) this entry is about — stamped from the
   *  class's currentCohortId at creation. Load-bearing for Phase 2 rollover:
   *  it's how a departed cohort's child-derived entries are told apart from the
   *  current group's. Unrecoverable if omitted, so it's always populated. */
  cohortId: string;
}

/** Expected working-noise level for a class — one of three named levels so the
 *  cover teacher knows whether talking is off-task or exactly what's wanted. */
export type WorkingNoise = 'silent' | 'quiet_discussion' | 'collaborative';

/** Continuity pillars carried on the Class — the things that distinguish
 *  THIS Year 11 Maths class from any other Year 11 Maths class anywhere. */
export interface ClassContinuityPillars {
  // ── Curriculum ──
  examBoard?: ExamBoard;
  /** Optional spec code — e.g. "8300" for AQA Maths GCSE. Free text since
   *  spec codes vary widely and we don't host the boards' content. */
  specCode?: string;
  tier?: ClassTier;
  /** Free text — what unit/topic they're currently on. The cover teacher's
   *  most useful single piece of context. Updated by the teacher every
   *  couple of weeks as they progress. */
  currentUnit?: string;
  pacing?: ClassPacing;

  // ── Classroom culture ──
  behaviourNorm?: string;
  /** The class's actual escalation ladder, so a cover teacher follows the real
   *  route rather than guessing. Template default offered in the editor:
   *  "Warning → move seat → on-call via [Key Contact]". */
  escalationRoute?: string;
  entryRoutine?: string;
  /** How the class is dismissed — the bookend to entryRoutine (packing away,
   *  stacking stools, equipment returned, waiting behind chairs). */
  exitRoutine?: string;
  /** Expected working-noise level, so the cover teacher can tell off-task noise
   *  from exactly what this class is meant to sound like. */
  workingNoise?: WorkingNoise;
  /** Optional nuance on the noise expectation — e.g. "silent for the starter,
   *  paired talk once they're working". */
  workingNoiseNote?: string;
  /** A short standing line the regular teacher wants read to the class at the
   *  start of ANY cover lesson — read aloud in the first 30 seconds. Class-level
   *  and standing: it's the only message path for emergency cover (never
   *  prompted at 7am) and leads the cover briefing. */
  messageFromTeacher?: string;
  pedagogyNorm?: string;
  equipment?: Equipment[];
  /** Free-text equipment items the teacher added when 'other' is toggled
   *  on — e.g. "mini whiteboards", "geometry sets", "dictionaries". Shown
   *  to the cover teacher alongside the preset items. */
  customEquipment?: string[];

  // ── Subject-specific (shown only when subject matches) ──
  calculatorPolicy?: CalculatorPolicy;
  targetLanguageRatio?: TargetLanguageRatio;
  kitPolicy?: string;
  labSafetyBriefing?: boolean;

  // ── Accumulating pillars (Phase 1 feedback loop) ──
  // Dated, attributed observations appended by cover feedback and curated by
  // the teacher. Arrays (not strings) from the start so appends never
  // string-concat and cohort archival (Phase 2) has per-entry tags to work
  // with. Teacher-facing editor fields + cover-briefing display land in
  // Phase 2; in Phase 1 they surface through the suggestion review banner.
  knownMisconceptions?: PillarEntry[];
  whatWorksWithThem?: PillarEntry[];
  /** "What can they already do / what haven't they met yet?" Feedback writes
   *  only canAlready in Phase 1; notMetYet is teacher-authored. */
  priorKnowledge?: { canAlready: PillarEntry[]; notMetYet: PillarEntry[] };

  // ── Escape hatch — conversational ──
  /** A single free-text answer to "Anything else that makes your class
   *  different?" — replaces the previous structured customPillars
   *  (label + value pairs) with one human-tone question. */
  otherDistinctives?: string;
  /** @deprecated retired in favour of otherDistinctives; left on the type
   *  so existing stored data deserialises without errors. New code must
   *  not write or read this field. */
  customPillars?: { id: string; label: string; value: string }[];
}

/** A Class represents a teaching group whose profile compounds over time. */
export interface ClassEntity {
  id: string;
  /** The school this class belongs to. Used as Cosmos partition key. */
  schoolId: string;
  /** Cosmos document type discriminator — distinguishes from Lesson docs. */
  type: 'class';

  /** Display label shown to teachers and admins — "9B French", "Year 4 Hare". */
  displayLabel: string;
  yearGroup: string;
  /** Subject — null for primary form classes that span all subjects. */
  subject: string | null;
  /** Set identifier ("Set 1", "B") — null when not setted. */
  setIdentifier: string | null;
  shape: ClassShape;

  /** When/where the class meets across the week pattern. */
  schedule: ClassMeetingSlot[];
  /** Default room (overridden per-slot if needed). */
  defaultRoom: string | null;

  /** Staff ID(s) of the regular teacher(s). Co-taught classes carry several. */
  regularTeacherIds: string[];

  /** ISO date this class was first established — anchors the cohort age.
   *  NOTE: this is a fixed *creation* stamp; it never advances, so it is NOT
   *  a valid source for currentCohortId on its own (see that field). */
  academicYearStarted: string;
  /** Optional shared ID across classes that share the same cohort of pupils
   *  (useful for following Year 9 → 10 → 11 as one group). Deliberately STABLE
   *  as a cohort ages up — the inverse of currentCohortId, not a source for it. */
  cohortGroupId?: string;
  /** The current cohort *instance* — the group of children in this room right
   *  now. Stamped onto every PillarEntry (via cohortIdForClass) so Phase 2
   *  rollover can archive a departed cohort's child-derived entries while
   *  structural pillars carry forward. Advanced ONLY by an explicit rollover
   *  (Phase 2), never by the calendar — Y12→Y13 keeps the same cohort, so a
   *  date-derived tag would wrongly bin still-valid entries. Optional for
   *  back-compat with stored classes; minted on create/save, with a stable
   *  fallback (academicYearStarted) at stamp time for any not yet re-saved. */
  currentCohortId?: string;

  /** Phase 2d.1 — continuity profile. Optional so existing classes
   *  continue to work; teachers/admins fill in over time. */
  continuityPillars?: ClassContinuityPillars;

  status: 'active' | 'archived';
  archivedAt?: number;

  createdAt: number;
  updatedAt: number;
  createdBy: string;
}

/** The pillar fields the feedback loop is allowed to target, and how each
 *  applies. Shared source of truth for the wizard, the apply logic, and the
 *  server-side allowlist (duplicated in the Functions package, which can't
 *  import from here). Legacy targets accept 'update' for back-compat. */
export const PILLAR_SUGGESTION_KINDS = ['append', 'update', 'flag'] as const;
export const PILLAR_SUGGESTION_FIELDS: PillarSuggestionField[] = [
  'pacing', 'knownMisconceptions', 'priorKnowledgeCanAlready', 'whatWorksWithThem',
  'behaviourNorm', 'entryRoutine', 'currentUnit', 'equipment', 'otherDistinctives',
];

/** Stable cohort tag for a class — the single source for stamping PillarEntry.
 *  Prefers the explicit currentCohortId; for a legacy class not yet re-saved,
 *  falls back to the (stable, per-class) academicYearStarted, then a constant.
 *  Never derives from the calendar: cohort change is a rollover event, not a
 *  date. Phase 2 rollover advances currentCohortId; everything downstream reads
 *  through here. */
export function cohortIdForClass(
  cls: Pick<ClassEntity, 'currentCohortId' | 'academicYearStarted'> | undefined,
): string {
  return cls?.currentCohortId || cls?.academicYearStarted || 'cohort-initial';
}

/** Build a PillarEntry, stamping the class's current cohort. */
export function makePillarEntry(
  text: string,
  cls: Pick<ClassEntity, 'currentCohortId' | 'academicYearStarted'> | undefined,
  opts: { source: PillarEntry['source']; attributedTo?: string; lessonId?: string },
): PillarEntry {
  return {
    id: uid('PE'),
    text: text.trim(),
    createdAt: Date.now(),
    source: opts.source,
    attributedTo: opts.attributedTo,
    lessonId: opts.lessonId,
    confirmCount: 1,
    cohortId: cohortIdForClass(cls),
  };
}

/** Conservative normalisation for exact-match dedup — deliberately NOT semantic.
 *  "struggled with negatives" and "kept getting negatives wrong" will NOT match,
 *  and that's accepted: under-dedup is a teacher merging two lines in seconds;
 *  over-dedup destroys a real signal and inflates confirmCount. */
export function normalisePillarText(s: string): string {
  return s.toLowerCase().replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Apply a resolved PillarSuggestion to a pillars object according to its
 *  `kind`. Pure — returns a new pillars object; the caller persists it.
 *   • flag   → returns pillars unchanged (surfaced for review, never written).
 *   • update → replaces a scalar (pacing; legacy string fields).
 *   • append → adds a PillarEntry to the target array, with exact-match dedup:
 *     a re-report of the same (normalised) text bumps confirmCount instead of
 *     duplicating — dedup and confidence in one mechanism.
 *  This is the single place the "no blind whole-field overwrite" guarantee is
 *  enforced. */
export function applySuggestionToPillars(
  pillars: ClassContinuityPillars,
  suggestion: PillarSuggestion,
  cls: Pick<ClassEntity, 'currentCohortId' | 'academicYearStarted'> | undefined,
): ClassContinuityPillars {
  const kind = suggestion.kind ?? 'update';
  const text = suggestion.suggestedValue;

  if (kind === 'flag') return pillars;

  // Equipment ALWAYS appends to customEquipment, regardless of kind. This must
  // sit before the kind branches: legacy equipment suggestions carry no kind
  // (→ 'update') and would otherwise blind-write the Equipment[] field with a
  // raw string, dropping the intended customEquipment append. `equipment` is
  // deliberately not a PillarEntry[] pillar (Phase 1 §10 — left on its append
  // path), so it's handled here, not in the append-entry switch below.
  if (suggestion.field === 'equipment') {
    const existingCustom = pillars.customEquipment ?? [];
    const additions = text.split(/[\n,]+/).map(s => s.trim()).filter(Boolean)
      .filter(s => !existingCustom.some(e => e.toLowerCase() === s.toLowerCase()));
    return { ...pillars, customEquipment: [...existingCustom, ...additions] };
  }

  if (kind === 'update') {
    if (suggestion.field === 'pacing') {
      return (text === 'on_track' || text === 'ahead' || text === 'behind')
        ? { ...pillars, pacing: text }
        : pillars;
    }
    // Legacy scalar string fields (entryRoutine / currentUnit / otherDistinctives).
    return { ...pillars, [suggestion.field]: text };
  }

  // kind === 'append'
  const appendEntry = (arr: PillarEntry[] | undefined): PillarEntry[] => {
    const list = arr ?? [];
    const norm = normalisePillarText(text);
    const match = list.find(e => !e.archivedAt && normalisePillarText(e.text) === norm);
    if (match) return list.map(e => e === match ? { ...e, confirmCount: e.confirmCount + 1 } : e);
    return [...list, makePillarEntry(text, cls, {
      source: 'cover_observation',
      attributedTo: suggestion.coverTeacherName,
      lessonId: suggestion.lessonId,
    })];
  };

  switch (suggestion.field) {
    case 'knownMisconceptions':
      return { ...pillars, knownMisconceptions: appendEntry(pillars.knownMisconceptions) };
    case 'whatWorksWithThem':
      return { ...pillars, whatWorksWithThem: appendEntry(pillars.whatWorksWithThem) };
    case 'priorKnowledgeCanAlready': {
      const pk = pillars.priorKnowledge ?? { canAlready: [], notMetYet: [] };
      return { ...pillars, priorKnowledge: { ...pk, canAlready: appendEntry(pk.canAlready) } };
    }
    default:
      return pillars; // unknown append target — server allowlist prevents this
  }
}

/** The three accumulating pillar arrays a teacher can curate on the class
 *  profile. Matches the append targets in applySuggestionToPillars. */
export type PillarCurateField = 'knownMisconceptions' | 'priorKnowledgeCanAlready' | 'priorKnowledgeNotMetYet' | 'whatWorksWithThem';

/** Merge several PillarEntry items the teacher has marked as "the same" into one.
 *  Pure. The canonical entry (whose wording is kept) is the most-confirmed of the
 *  set, tie-broken by most recent; it absorbs the SUM of every merged entry's
 *  confirmCount — so combining three separately-worded reports of one pattern
 *  finally surfaces the "seen by 3 covers" signal that exact-match dedup can't.
 *
 *  The merged-away entries are ARCHIVED (archivedAt set), never hard-deleted:
 *  they may contain pupil names/details, so the record must survive for a SAR
 *  and the wording isn't lost — merge is consolidation, not erasure. They drop
 *  out of the display (which filters archivedAt) but stay in the doc.
 *  Fewer than two live ids, or ids not present, is a no-op. */
export function mergePillarEntries(entries: PillarEntry[], ids: string[]): PillarEntry[] {
  const idSet = new Set(ids);
  const toMerge = entries.filter(e => idSet.has(e.id) && !e.archivedAt);
  if (toMerge.length < 2) return entries;
  const canonical = toMerge.reduce((best, e) => {
    if (e.confirmCount > best.confirmCount) return e;
    if (e.confirmCount === best.confirmCount && e.createdAt > best.createdAt) return e;
    return best;
  });
  const totalCount = toMerge.reduce((sum, e) => sum + e.confirmCount, 0);
  const now = Date.now();
  return entries.map(e => {
    if (e.id === canonical.id) return { ...e, confirmCount: totalCount };
    if (idSet.has(e.id) && !e.archivedAt) return { ...e, archivedAt: now };
    return e;
  });
}

/** Remove PillarEntry items by id (teacher clearing noise / test data). Pure. */
export function removePillarEntries(entries: PillarEntry[], ids: string[]): PillarEntry[] {
  const idSet = new Set(ids);
  return entries.filter(e => !idSet.has(e.id));
}

/** Apply a curate action (merge / remove) to the right pillar array on a pillars
 *  object. Pure — returns a new pillars object the caller persists. */
export type PillarCurateAction = 'merge' | 'remove' | 'restore' | 'request_retrieval';

export function curatePillarEntries(
  pillars: ClassContinuityPillars,
  field: PillarCurateField,
  action: PillarCurateAction,
  ids: string[],
  by?: string,
): ClassContinuityPillars {
  const idSet = new Set(ids);
  const apply = (arr: PillarEntry[] | undefined): PillarEntry[] => {
    const list = arr ?? [];
    switch (action) {
      case 'merge': return mergePillarEntries(list, ids);
      case 'remove': return removePillarEntries(list, ids);
      case 'restore':
        // Admin un-archives: entry returns to the live profile; any teacher
        // retrieval request is cleared (it's been actioned). Only touches
        // entries that are actually archived (symmetric with request_retrieval).
        return list.map(e => (idSet.has(e.id) && e.archivedAt)
          ? { ...e, archivedAt: undefined, retrievalRequestedAt: undefined, retrievalRequestedBy: undefined }
          : e);
      case 'request_retrieval':
        // Teacher flags an ARCHIVED entry so an admin sees the ask. Only marks
        // archived entries; live ones are ignored.
        return list.map(e => (idSet.has(e.id) && e.archivedAt)
          ? { ...e, retrievalRequestedAt: Date.now(), retrievalRequestedBy: by }
          : e);
      default: return list;
    }
  };
  switch (field) {
    case 'knownMisconceptions':
      return { ...pillars, knownMisconceptions: apply(pillars.knownMisconceptions) };
    case 'whatWorksWithThem':
      return { ...pillars, whatWorksWithThem: apply(pillars.whatWorksWithThem) };
    case 'priorKnowledgeCanAlready': {
      const pk = pillars.priorKnowledge ?? { canAlready: [], notMetYet: [] };
      return { ...pillars, priorKnowledge: { ...pk, canAlready: apply(pk.canAlready) } };
    }
    case 'priorKnowledgeNotMetYet': {
      const pk = pillars.priorKnowledge ?? { canAlready: [], notMetYet: [] };
      return { ...pillars, priorKnowledge: { ...pk, notMetYet: apply(pk.notMetYet) } };
    }
    default:
      return pillars;
  }
}

/** How many archived observations on a class have an open teacher retrieval
 *  request. Lets the admin's class list flag which classes need attention
 *  without opening each one. */
export function countRetrievalRequests(pillars: ClassContinuityPillars | undefined): number {
  const p = pillars ?? {};
  const arrays = [
    p.knownMisconceptions,
    p.whatWorksWithThem,
    p.priorKnowledge?.canAlready,
    p.priorKnowledge?.notMetYet,
  ];
  return arrays.reduce((n, arr) => n + (arr ?? []).filter(e => e.retrievalRequestedAt).length, 0);
}

/** How many ARCHIVED observations a class holds — the entries the retention
 *  review acts on. Drives the bulk review screen's per-class counts. */
export function countArchivedEntries(pillars: ClassContinuityPillars | undefined): number {
  const p = pillars ?? {};
  const arrays = [
    p.knownMisconceptions,
    p.whatWorksWithThem,
    p.priorKnowledge?.canAlready,
    p.priorKnowledge?.notMetYet,
  ];
  return arrays.reduce((n, arr) => n + (arr ?? []).filter(e => e.archivedAt).length, 0);
}

/** Roll a class into a new cohort — the Phase 2 lifecycle mechanic that keeps a
 *  profile from growing forever and governs child-derived data across groups.
 *
 *  `childrenChanged` is the load-bearing decision (the Y12→Y13 case): a class can
 *  advance an academic year while keeping the SAME students, in which case their
 *  observations are still valid and must NOT be touched.
 *   • childrenChanged: false — same group, new year. No-op on the data; the
 *     observations carry on. (Just stamps updatedAt.)
 *   • childrenChanged: true — a new group. The child-derived observations
 *     (misconceptions, prior knowledge, what-works) belonged to the departed
 *     cohort, so they are ARCHIVED (archivedAt set — retained for a SAR, never
 *     hard-deleted here; hidden from the active profile). Structural pillars
 *     (routines, escalation, working noise, static curriculum context, the
 *     teacher's own scalars) carry forward untouched. A fresh currentCohortId is
 *     minted so new observations tag to the new group.
 *  Pure; returns the updated class. Purge of archived data is a separate,
 *  policy-driven step (not done here). */
export function rollOverClassCohort(
  cls: ClassEntity,
  opts: { childrenChanged: boolean },
): ClassEntity {
  const now = Date.now();
  if (!opts.childrenChanged) {
    return { ...cls, updatedAt: now };
  }
  const pillars = cls.continuityPillars;
  const newCohortId = uid('COH');
  if (!pillars) return { ...cls, currentCohortId: newCohortId, updatedAt: now };
  const archiveArr = (arr: PillarEntry[] | undefined): PillarEntry[] | undefined =>
    arr?.map(e => (e.archivedAt ? e : { ...e, archivedAt: now }));
  const nextPillars: ClassContinuityPillars = {
    ...pillars,
    knownMisconceptions: archiveArr(pillars.knownMisconceptions),
    whatWorksWithThem: archiveArr(pillars.whatWorksWithThem),
    priorKnowledge: pillars.priorKnowledge
      ? {
          canAlready: archiveArr(pillars.priorKnowledge.canAlready) ?? [],
          notMetYet: archiveArr(pillars.priorKnowledge.notMetYet) ?? [],
        }
      : pillars.priorKnowledge,
  };
  return { ...cls, continuityPillars: nextPillars, currentCohortId: newCohortId, updatedAt: now };
}

/** Helper: which subject-specific pillars apply for a given subject string. */
export function getApplicablePillarsForSubject(subject: string | null | undefined): {
  calculator: boolean;
  targetLanguage: boolean;
  labSafety: boolean;
  kit: boolean;
} {
  if (!subject) return { calculator: false, targetLanguage: false, labSafety: false, kit: false };
  const s = subject.toLowerCase().trim();
  return {
    calculator: SUBJECTS_USING_CALCULATOR_POLICY.some(sub => s.includes(sub)),
    targetLanguage: SUBJECTS_USING_TARGET_LANGUAGE.some(sub => s.includes(sub)),
    labSafety: SUBJECTS_USING_LAB_SAFETY.some(sub => s.includes(sub)),
    kit: SUBJECTS_USING_KIT_POLICY.some(sub => s.includes(sub)),
  };
}

/** Helper: count how many pillars are populated — used in the class card
 *  to show a "5/N pillars" completeness chip.
 *  NOTE: `pedagogyNorm` is deliberately NOT counted (D2 — cut from the pillar
 *  set; still read on the type for legacy display, never counted so bands stay
 *  comparable across classes of different ages). */
export function countPillars(pillars: ClassContinuityPillars | undefined, subject: string | null | undefined): { set: number; total: number } {
  if (!pillars) {
    const applicable = getApplicablePillarsForSubject(subject);
    // Universal: examBoard, tier, currentUnit, pacing, behaviourNorm,
    // escalationRoute, entryRoutine, exitRoutine, workingNoise,
    // messageFromTeacher, equipment, otherDistinctives = 12
    let total = 12;
    if (applicable.calculator) total += 1;
    if (applicable.targetLanguage) total += 1;
    if (applicable.labSafety) total += 1;
    if (applicable.kit) total += 1;
    return { set: 0, total };
  }
  const applicable = getApplicablePillarsForSubject(subject);
  let total = 0;
  let set = 0;
  const check = (val: unknown) => {
    total += 1;
    if (val !== undefined && val !== null && val !== '' && !(Array.isArray(val) && val.length === 0)) set += 1;
  };
  // Universal (pedagogyNorm intentionally excluded — see D2)
  check(pillars.examBoard);
  check(pillars.tier);
  check(pillars.currentUnit);
  check(pillars.pacing);
  check(pillars.behaviourNorm);
  check(pillars.escalationRoute);
  check(pillars.entryRoutine);
  check(pillars.exitRoutine);
  check(pillars.workingNoise);
  check(pillars.messageFromTeacher);
  check(pillars.equipment);
  check(pillars.otherDistinctives);
  // Subject-specific
  if (applicable.calculator) check(pillars.calculatorPolicy);
  if (applicable.targetLanguage) check(pillars.targetLanguageRatio);
  if (applicable.labSafety) check(pillars.labSafetyBriefing);
  if (applicable.kit) check(pillars.kitPolicy);
  return { set, total };
}

/** A class's continuity "state band" — the weighted completeness signal shown
 *  on the class list. Unlike the raw N/M chip, this weights by continuity value
 *  (the curricular core carries most), so "Rich" means the pillars that
 *  actually help a cover teacher are filled, not just any twelve boxes. */
export type PillarBand = 'thin' | 'developing' | 'rich';

export function pillarStateBand(pillars: ClassContinuityPillars | undefined): {
  band: PillarBand; score: number; max: number;
} {
  const p = pillars ?? {};
  const hasText = (v: unknown) => v !== undefined && v !== null && v !== '';
  const hasEntries = (arr?: PillarEntry[]) => !!arr && arr.some(e => !e.archivedAt);
  let score = 0;
  // weight 3 — the curricular core: the single most useful things to a cover
  if (hasText(p.currentUnit)) score += 3;
  if (hasText(p.pacing)) score += 3;
  if (hasEntries(p.knownMisconceptions)) score += 3;
  if (hasEntries(p.priorKnowledge?.canAlready)) score += 3;
  // weight 2 — behaviour standard, entry/exit routine (either), what-works
  if (hasText(p.behaviourNorm)) score += 2;
  if (hasText(p.entryRoutine) || hasText(p.exitRoutine)) score += 2;
  if (hasEntries(p.whatWorksWithThem)) score += 2;
  // weight 1 — operational + relational + static context
  if (hasText(p.workingNoise)) score += 1;
  if (hasText(p.escalationRoute)) score += 1;
  if (hasText(p.messageFromTeacher)) score += 1;
  if (hasText(p.examBoard) || hasText(p.tier)) score += 1;
  const max = 22; // 3·4 + 2·3 + 1·4
  const band: PillarBand = score >= 14 ? 'rich' : score >= 6 ? 'developing' : 'thin';
  return { band, score, max };
}

/** Visibility tier for a pupil flag — controls who can read it.
 *  - 'cover': appears in the cover-teacher brief (and to all school staff).
 *  - 'school': appears to school staff only, not external cover.
 *  - 'dsl_only': Designated Safeguarding Lead access only. Never in AI prompts. */
export type PupilFlagVisibility = 'cover' | 'school' | 'dsl_only';

/** Category of a pupil flag — drives the icon/colour in the UI. */
export type PupilFlagCategory =
  | 'behaviour'
  | 'send'
  | 'medical'
  | 'wellbeing'
  | 'grouping'
  | 'safeguarding';

/** How the flag entered the profile — used for provenance + audit. */
export type PupilFlagSource =
  | 'teacher_proactive'
  | 'cover_observation_confirmed'
  | 'admin_event';

export interface PupilFlag {
  id: string;
  category: PupilFlagCategory;
  visibility: PupilFlagVisibility;
  /** The note itself, kept in the teacher's own words for attribution. */
  note: string;
  /** Teacher whose words these are — drives the "Ms Patel says: …" attribution. */
  attributedToTeacherId: string;
  source: PupilFlagSource;

  createdAt: number;
  /** Updated each time a teacher re-confirms the flag via Monday review. */
  lastConfirmedAt: number;
  /** Optional expiry for naturally time-bound flags (e.g. a medical course). */
  expiresAt?: number;
  status: 'active' | 'archived';
}

/** A pupil's membership of a single class with a lifecycle window. */
export interface PupilClassMembership {
  classId: string;
  joinedDate: string;
  /** ISO date the pupil left this class — null while still a member. */
  leftDate?: string;
}

/** A Pupil is a child record carried at school level across years. */
export interface PupilEntity {
  id: string;
  schoolId: string;
  type: 'pupil';

  /** First name only — what the cover teacher sees. Used in AI prompts. */
  displayFirstName: string;
  /** Full legal name — admin/DSL tier only. Never sent to AI prompts. */
  fullName: string;

  enrolledFrom: string;
  /** ISO date the pupil left the school — null while currently enrolled. */
  enrolledUntil?: string;

  classMemberships: PupilClassMembership[];
  flags: PupilFlag[];

  status: 'active' | 'archived';
  createdAt: number;
  updatedAt: number;
}

export interface AppState {
  school: School;
  lessons: Lesson[];
  feedback: Feedback[];
  /** Classes loaded from the backend. Populated by school admin or migration. */
  classes?: ClassEntity[];
  /** Pupils loaded from the backend. Populated by school admin. */
  pupils?: PupilEntity[];
  currentUser: {
    type: 'school' | 'teacher' | 'cover' | null;
    id: string;
    name: string;
    department?: string;
    email?: string;
  } | null;
}

export interface TutorialState {
  completed: boolean;
  currentStep: number;
  stepsViewed: number[];
  skipped: boolean;
}

// Tutorial step definitions (legacy - now in TutorialOverlay.tsx)
// Keeping for backwards compatibility
export const TUTORIAL_STEPS = [
  { id: 1, target: 'create-lesson-btn', title: 'Create Your First Lesson', description: 'Click here to start creating a cover lesson.', position: 'bottom' as const },
  { id: 2, target: 'coverage-setup', title: 'Select Coverage Type', description: 'Choose the type of cover needed.', position: 'right' as const },
  { id: 3, target: 'template-selector', title: 'Choose a Template', description: 'Pick a pre-built template to save time.', position: 'bottom' as const },
  { id: 4, target: 'lesson-details', title: 'Fill in Details', description: 'Add your class details, lesson aim, and objectives.', position: 'right' as const },
  { id: 5, target: 'lesson-steps', title: 'Add Lesson Steps', description: 'Break down your lesson into timed steps.', position: 'right' as const },
  { id: 6, target: 'save-draft-btn', title: 'Save as Draft', description: 'Save your work to continue later.', position: 'left' as const },
  { id: 7, target: 'publish-btn', title: 'Publish Your Lesson', description: 'Make the lesson available to cover teachers.', position: 'left' as const },
  { id: 8, target: 'clone-btn', title: 'Clone Lessons', description: 'Duplicate existing lessons to save time.', position: 'bottom' as const },
];

// Generate a secure invite code
export function generateSecureCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

// Save invite codes for teachers
export function saveInviteCodes(codes: InviteCode[]): void {
  try {
    localStorage.setItem(INVITE_CODES_KEY, JSON.stringify(codes));
  } catch (e) {
    console.error('Failed to save invite codes:', e);
  }
}

// Get all invite codes
export function getInviteCodes(): InviteCode[] {
  try {
    const stored = localStorage.getItem(INVITE_CODES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    console.error('Failed to load invite codes:', e);
    return [];
  }
}

// Validate an invite code and return teacher info
export function validateInviteCode(code: string): { valid: boolean; teacher?: InviteCode } {
  const codes = getInviteCodes();
  const found = codes.find(c => c.code === code && c.isValid && !c.usedAt);
  
  if (found) {
    return { valid: true, teacher: found };
  }
  return { valid: false };
}

// Mark a code as used
export function markCodeAsUsed(code: string): void {
  const codes = getInviteCodes();
  const updated = codes.map(c => 
    c.code === code ? { ...c, usedAt: Date.now() } : c
  );
  saveInviteCodes(updated);
}

// Generate codes for all staff members when school setup completes
export function generateInviteCodesForStaff(staff: StaffMember[], schoolId: string = 'default'): InviteCode[] {
  const existingCodes = getInviteCodes();
  const newCodes: InviteCode[] = [];

  for (const member of staff) {
    // Check if code already exists for this email
    const existing = existingCodes.find(c => c.email === member.email);
    if (existing) {
      newCodes.push(existing);
    } else {
      const inviteCode: InviteCode = {
        code: generateSecureCode(),
        email: member.email,
        teacherName: `${member.firstName} ${member.lastName}`,
        department: member.department,
        schoolId,
        createdAt: Date.now(),
        isValid: true,
      };
      newCodes.push(inviteCode);
    }
  }

  saveInviteCodes([...existingCodes.filter(c => c.schoolId !== schoolId), ...newCodes]);
  return newCodes;
}

// Get tutorial state
export function getTutorialState(): TutorialState {
  try {
    const stored = localStorage.getItem(TUTORIAL_STATE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to load tutorial state:', e);
  }
  return {
    completed: false,
    currentStep: 0,
    stepsViewed: [],
    skipped: false,
  };
}

/**
 * School/admin tour state.
 *
 * Falls back to the old shared key on first read and adopts it, so admins who
 * have already done the tour don't get it again just because the key moved —
 * which would have caused the exact replay this split is fixing.
 */
/**
 * Did an admin on this browser previously finish or skip the school tour?
 *
 * Keyed off the portal-nudge record CMCApp writes from armPortalNudge(), which
 * fires on both exits from the school tour and is never written by the teacher
 * or cover flows. Browser-scoped rather than user-scoped, which is the right
 * grain here: the question is "has the old shared key been written by an ADMIN
 * on this machine", not "by which admin".
 */
function adminFinishedATourOnThisBrowser(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('cmc_admin_portal_nudge_')) return true;
    }
  } catch {
    // Private browsing — fall through to "no", which costs one replay at most.
  }
  return false;
}

export function getSchoolTutorialState(): TutorialState {
  const blank: TutorialState = { completed: false, currentStep: 0, stepsViewed: [], skipped: false };
  try {
    const own = localStorage.getItem(SCHOOL_TUTORIAL_STATE_KEY);
    if (own) return JSON.parse(own);
    // Migration from the old shared key — but ONLY when the verdict can be
    // attributed to an admin on this browser.
    //
    // The legacy key is the TEACHER tour's. Adopting any completed verdict from
    // it looked harmless and was the opposite of harmless: a teacher finishing
    // their tour on a shared school machine would have marked the SCHOOL tour
    // done, so the next admin to log in silently lost their first-run tour. That
    // is a worse failure than the replay this migration exists to prevent, and
    // it is the same cross-contamination the key split is fixing, pointed the
    // other way.
    //
    // `cmc_admin_portal_nudge_*` is the attributable signal: armPortalNudge()
    // writes it on both exits from the school tour (complete AND skip) and
    // nothing in the teacher flow touches it. Its presence means an admin on
    // this browser genuinely finished or skipped the tour under the old key.
    // Admins who did so before that nudge existed get one replay — the safe
    // direction, and the server flag retires it for good after that.
    if (adminFinishedATourOnThisBrowser()) {
      const legacy = localStorage.getItem(TUTORIAL_STATE_KEY);
      if (legacy) {
        const parsed = JSON.parse(legacy) as TutorialState;
        // Only the verdict. Copying a half-finished step index across would drop
        // an admin into the middle of a tour they never started.
        if (parsed.completed || parsed.skipped) {
          const adopted: TutorialState = { ...blank, completed: parsed.completed, skipped: parsed.skipped };
          localStorage.setItem(SCHOOL_TUTORIAL_STATE_KEY, JSON.stringify(adopted));
          return adopted;
        }
      }
    }
  } catch (e) {
    console.error('Failed to load school tutorial state:', e);
  }
  return blank;
}

export function saveSchoolTutorialState(state: TutorialState): void {
  try {
    localStorage.setItem(SCHOOL_TUTORIAL_STATE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error('Failed to save school tutorial state:', e);
  }
}

// Save tutorial state
export function saveTutorialState(state: TutorialState): void {
  try {
    localStorage.setItem(TUTORIAL_STATE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error('Failed to save tutorial state:', e);
  }
}

// Get cover tutorial state
export function getCoverTutorialState(): TutorialState {
  try {
    const stored = localStorage.getItem(COVER_TUTORIAL_STATE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to load cover tutorial state:', e);
  }
  return {
    completed: false,
    currentStep: 0,
    stepsViewed: [],
    skipped: false,
  };
}

// Save cover tutorial state
export function saveCoverTutorialState(state: TutorialState): void {
  try {
    localStorage.setItem(COVER_TUTORIAL_STATE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error('Failed to save cover tutorial state:', e);
  }
}

// Build app state from onboarding data
export function buildAppStateFromOnboarding(): AppState {
  const onboardingState = getOnboardingState();
  const schoolData = onboardingState.schoolData;

  // Map wizard PolicyDocument[] to the named slots School.policies uses in settings.
  // Anti-Bullying is behaviour-adjacent so it maps to behaviourPolicy.
  // All other types that don't match a named slot become additionalPolicies.
  const wizardPolicies = (schoolData.policies ?? []).filter(p => p.fileName);

  // The wizard no longer asks for a type — policies are bulk-uploaded and named
  // from their filenames — so the slot each one belongs in is derived. An
  // explicit `type` still wins where it exists, because legacy records and the
  // Settings editor both set it deliberately and a human's answer beats a
  // keyword match.
  const EXPLICIT_TYPES: Record<string, PolicySlotKind> = {
    behaviour: 'behaviour', 'anti-bullying': 'behaviour',
    safeguarding: 'safeguarding',
    cover: 'cover',
  };
  const slotFor = (p: PolicyDocument): PolicySlotKind =>
    EXPLICIT_TYPES[p.type?.toLowerCase().trim() ?? ''] ?? classifyPolicyFileName(p.fileName);

  // First match claims the slot; later ones fall through to additionalPolicies
  // rather than overwriting. Two behaviour policies is a real thing (main plus
  // a sixth-form annexe) and silently discarding the second would be worse than
  // listing it separately.
  const claimed: Partial<Record<Exclude<PolicySlotKind, ''>, PolicyDocument>> = {};
  const extras: PolicyDocument[] = [];
  for (const p of wizardPolicies) {
    const slot = slotFor(p);
    if (slot && !claimed[slot]) claimed[slot] = p;
    else extras.push(p);
  }
  const behaviourDoc = claimed.behaviour;
  const safeguardingDoc = claimed.safeguarding;
  const coverDoc = claimed.cover;
  const additionalPolicies = extras.map(p => ({
    type: p.type || 'Other',
    name: policyDisplayName(p),
    fileName: p.fileName,
    keyPoints: '',
  }));
  const mappedPolicies: School['policies'] = {
    ...(behaviourDoc?.fileName ? { behaviourPolicy: { fileName: behaviourDoc.fileName, keyPoints: '' } } : {}),
    ...(safeguardingDoc?.fileName ? { safeguardingPolicy: { fileName: safeguardingDoc.fileName, keyPoints: '' } } : {}),
    ...(coverDoc?.fileName ? { coverPolicy: { fileName: coverDoc.fileName, keyPoints: '' } } : {}),
    ...(additionalPolicies.length > 0 ? { additionalPolicies } : {}),
  };

  // Map periods from onboarding to app format (use periodConfig if available)
  const periodConfig = schoolData.periodConfig;
  const toPeriods = (cfg: typeof periodConfig | undefined, fallbackNames?: string[]): Period[] => {
    if (cfg?.periods?.length) {
      return cfg.periods.map((p, idx) => ({
        number: String(idx + 1),
        startTime: p.startTime,
        endTime: p.endTime,
      }));
    }
    return (fallbackNames || []).map((_, idx) => ({
      number: String(idx + 1),
      startTime: `${8 + idx}:30`,
      endTime: `${9 + idx}:30`,
    }));
  };
  const periods: Period[] = toPeriods(periodConfig, schoolData.periods);

  // Multi-week timetable: build the per-week period arrays so date-based lookup works at runtime.
  const weekPattern = schoolData.weekPattern && schoolData.weekPattern > 1 ? schoolData.weekPattern : 1;
  const weekPeriods: Period[][] | undefined = weekPattern > 1
    ? [periods, ...((schoolData.additionalWeeks || []).slice(0, weekPattern - 1).map(c => toPeriods(c, schoolData.periods)))]
    : undefined;

  // Get the theme color from onboarding
  const primaryColor = schoolData.primaryColor || '#1e3a5f';

  const school: School = {
    name: schoolData.schoolName || 'Your School',
    logo: '🏫',
    logoImage: schoolData.logoUrl || null,
    primary: primaryColor,
    urn: schoolData.urn,
    dfeNumber: schoolData.dfeNumber,
    address: schoolData.address,
    postcode: schoolData.postcode,
    staff: schoolData.staff || [],
    subjects: schoolData.subjects || [],
    rooms: schoolData.rooms || [],
    yearGroups: schoolData.yearGroups || [],
    periods,
    ...(weekPattern > 1 ? { weekPattern, weekPeriods, weekAnchorDate: schoolData.weekAnchorDate } : {}),
    contacts: {
      adminEmail: schoolData.contactEmail || '',
      headTeacher: {
        name: `${schoolData.contactTitle || ''} ${schoolData.contactFirstName || ''} ${schoolData.contactLastName || ''}`.trim(),
        email: schoolData.contactEmail || '',
        phone: schoolData.contactPhone || '',
      },
      secondaryContacts: (schoolData.secondaryContacts || []).map(c => ({
        name: c.name,
        email: c.email,
        role: c.role,
        position: c.role,
        phone: c.phone ?? '',
      })),
    },
    guestAccount: schoolData.guestAccount,
    policies: Object.keys(mappedPolicies).length > 0 ? mappedPolicies : undefined,
    departments: schoolData.departments && schoolData.departments.length > 0 ? schoolData.departments : undefined,
    observationRetention: schoolData.observationRetention,
  };

  return {
    school,
    lessons: [],
    feedback: [],
    currentUser: null,
  };
}

// Save app state
export function saveAppState(state: AppState): void {
  try {
    localStorage.setItem(APP_STATE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error('Failed to save app state:', e);
  }
}

// Get app state
export function getAppState(): AppState | null {
  try {
    const stored = localStorage.getItem(APP_STATE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to load app state:', e);
  }
  return null;
}

// Reset all app data
export function resetAppState(): void {
  localStorage.removeItem(APP_STATE_KEY);
  localStorage.removeItem(INVITE_CODES_KEY);
  localStorage.removeItem(TUTORIAL_STATE_KEY);
  localStorage.removeItem(SCHOOL_TUTORIAL_STATE_KEY);
  localStorage.removeItem(COVER_TUTORIAL_STATE_KEY);
}

// Utility function to generate unique IDs
export function uid(prefix = 'X'): string {
  return `${prefix}-${Math.floor(1000 + Math.random() * 900000)}-${Date.now().toString(36)}`;
}

// Get today's date in ISO format
export function getTodayISO(): string {
  return new Date().toISOString().split('T')[0];
}

// ============= COVER ASSIGNMENTS =============

export interface CoverAssignment {
  id: string;
  lessonId: string;
  coverTeacherId: string;
  coverTeacherName: string;
  assignedAt: number;
}

// ============= EMERGENCY ALERTS =============

export interface EmergencyAlert {
  id: string;
  lessonId: string;
  schoolId: string;           // Required for Cosmos DB partition key + push targeting
  coverTeacherId: string;
  coverTeacherName: string;
  classroom: string;
  subject: string;
  message: string;
  // pending   → waiting for any admin to respond
  // snoozed   → one admin snoozed, still waiting
  // accepted  → one admin accepted; alarm clears for everyone
  // cancelled → cover teacher or admin cancelled the alert
  status: 'pending' | 'snoozed' | 'accepted' | 'cancelled';
  createdAt: number;
  snoozeUntil?: number;
  // Populated once an admin accepts
  acceptedById?: string;
  acceptedByName?: string;   // e.g. "Jane Smith"
  acceptedByTitle?: string;  // e.g. "Mrs" — from school contacts/staff title
  acceptedAt?: number;
  cancelledAt?: number;
}

export function saveEmergencyAlert(alert: EmergencyAlert): void {
  try {
    const existing = getEmergencyAlerts();
    existing.push(alert);
    localStorage.setItem(EMERGENCY_ALERTS_KEY, JSON.stringify(existing));
  } catch (e) {
    console.error('Failed to save emergency alert:', e);
  }
}

export function getEmergencyAlerts(): EmergencyAlert[] {
  try {
    const stored = localStorage.getItem(EMERGENCY_ALERTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function updateEmergencyAlert(id: string, updates: Partial<EmergencyAlert>): void {
  try {
    const alerts = getEmergencyAlerts();
    const updated = alerts.map(a => a.id === id ? { ...a, ...updates } : a);
    localStorage.setItem(EMERGENCY_ALERTS_KEY, JSON.stringify(updated));
  } catch (e) {
    console.error('Failed to update emergency alert:', e);
  }
}

export function getPendingEmergencyAlerts(): EmergencyAlert[] {
  const now = Date.now();
  return getEmergencyAlerts().filter(a =>
    a.status === 'pending' ||
    (a.status === 'snoozed' && a.snoozeUntil && a.snoozeUntil <= now)
  );
}

export function getAcceptedEmergencyAlerts(): EmergencyAlert[] {
  return getEmergencyAlerts().filter(a => a.status === 'accepted');
}
